import React from "react";
import {
  Alert,
  Box,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

/**
 * Renders a request's dry-run preview. The backend returns a free-form object
 * so we render whatever it gives us with sensible fall-throughs.
 */
const DryRunPanel = ({ preview }) => {
  if (!preview) {
    return (
      <Typography variant="caption" color="text.secondary">
        No dry-run preview yet.
      </Typography>
    );
  }

  const {
    estimated_seconds,
    reversibility,
    blast_radius,
    side_effects,
    changeset,
    missing_permissions,
    ...rest
  } = preview;

  return (
    <Box>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
        {estimated_seconds != null && (
          <Chip
            size="small"
            label={`~${estimated_seconds}s`}
            sx={{ backgroundColor: "grey.100" }}
          />
        )}
        {reversibility && (
          <Chip
            size="small"
            label={`Reversibility: ${reversibility}`}
            sx={{ backgroundColor: "grey.100" }}
          />
        )}
        {blast_radius && (
          <Chip
            size="small"
            label={`Blast radius: ${blast_radius}`}
            sx={{ backgroundColor: "grey.100" }}
          />
        )}
      </Box>

      {side_effects && side_effects.length > 0 && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Side effects</Typography>
          <Box component="ul" sx={{ mt: 0.5, mb: 1.5, pl: 2.5, fontSize: 13 }}>
            {side_effects.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </Box>
        </>
      )}

      {missing_permissions && missing_permissions.length > 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, display: "block", mb: 0.5 }}>
            Missing permissions
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, fontSize: 12.5 }}>
            {missing_permissions.map((p, i) => (
              <li key={i}><code>{p}</code></li>
            ))}
          </Box>
        </Alert>
      )}

      {changeset && changeset.length > 0 && (
        <>
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Changeset</Typography>
          <TableContainer sx={{ mt: 0.5, mb: 1.5, border: 1, borderColor: "divider", borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: "grey.50" }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Operation</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Before</TableCell>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>After</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {changeset.map((c, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {c.operation || "—"}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {typeof c.before === "object" ? JSON.stringify(c.before) : String(c.before ?? "—")}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {typeof c.after === "object" ? JSON.stringify(c.after) : String(c.after ?? "—")}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {Object.keys(rest).length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="caption" sx={{ fontWeight: 600 }}>Other metadata</Typography>
          <Box
            component="pre"
            sx={{
              mt: 0.5,
              p: 1,
              backgroundColor: "grey.50",
              fontSize: 11.5,
              borderRadius: 1,
              maxHeight: 200,
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(rest, null, 2)}
          </Box>
        </>
      )}
    </Box>
  );
};

export default DryRunPanel;
